import { motion } from 'framer-motion';

interface AdvertisePlansProps {
  onSelectPlan: (plan: string) => void;
}

const plans = [
  {
    name: 'Spot Plan',
    duration: 'From 1 week',
    price: '$5,000',
    priceNote: '~/week',
    description: 'A short-term plan ideal for events and promotions',
    features: [
      'Available from 1 week',
      '8-hour daily display slot',
      'Creative production support',
      'Campaign report included',
    ],
    color: 'from-gray-600 to-gray-800',
    popular: false,
  },
  {
    name: 'Standard Plan',
    duration: 'From 1 month',
    price: '$15,000',
    priceNote: '~/month',
    description: 'The best plan for sustained brand exposure',
    features: [
      'Available from 1 month',
      '12-hour daily display slot',
      'Creative production included',
      'Weekly reports provided',
      'Priority location selection',
    ],
    color: 'from-electric-blue to-cyan-400',
    popular: true,
  },
  {
    name: 'Premium Plan',
    duration: 'From 3 months',
    price: '$35,000',
    priceNote: '~/month',
    description: 'An enterprise plan for maximum exposure',
    features: [
      'Available from 3 months',
      '24-hour full display',
      'Guaranteed premium locations',
      'Dedicated account manager',
      'Real-time analytics dashboard',
      'Competitor exclusivity option',
    ],
    color: 'from-neon-gold to-amber-500',
    popular: false,
  },
];

export default function AdvertisePlans({ onSelectPlan }: AdvertisePlansProps) {
  const handleSelectPlan = (planName: string) => {
    try {
      onSelectPlan(planName);
      const formElement = document.getElementById('inquiry-form');
      if (formElement) {
        formElement.scrollIntoView({ behavior: 'smooth' });
      }
    } catch (error) {
      console.error('Error handling plan selection:', error);
    }
  };

  return (
    <section
      id="plans"
      className="py-12 px-6 relative"
      style={{
        backgroundImage:
          "url('https://public.readdy.ai/ai/img_res/b421b1a906d45857dec96967ee2c320a.jpg')",
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    >
      <div className="absolute inset-0 bg-white/90 backdrop-blur-sm"></div>
      <div className="max-w-[1400px] mx-auto relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-8"
        >
          <span className="text-electric-blue font-medium text-xs uppercase tracking-wider">
            Pricing Plans
          </span>
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mt-2 mb-2">
            Advertising Plans
          </h2>
          <p className="text-gray-600 text-sm max-w-2xl mx-auto">
            Choose the plan that best fits your goals and budget
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {plans.map((plan, index) => (
            <motion.div
              key={plan.name}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className={`relative rounded-2xl overflow-hidden bg-white ${
                plan.popular ? 'ring-2 ring-electric-blue shadow-xl scale-105' : 'shadow-lg'
              }`}
            >
              {plan.popular && (
                <div className="absolute top-0 left-0 right-0 bg-gradient-to-r from-electric-blue to-cyan-400 text-white text-center py-1.5 text-xs font-medium">
                  Most Popular
                </div>
              )}
              <div className={`p-5 ${plan.popular ? 'pt-10' : ''}`}>
                <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${plan.color} flex items-center justify-center mb-4`}>
                  <i className="ri-advertisement-line text-lg text-white"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-1">{plan.name}</h3>
                <p className="text-gray-500 text-xs mb-3">{plan.duration}</p>
                <div className="mb-4">
                  <span className="text-2xl font-black text-gray-900">{plan.price}</span>
                  <span className="text-gray-500 text-xs">{plan.priceNote}</span>
                </div>
                <p className="text-gray-600 text-xs mb-4">{plan.description}</p>
                <ul className="space-y-2 mb-5">
                  {plan.features.map((feature, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <i className="ri-check-line text-electric-blue text-sm mt-0.5"></i>
                      <span className="text-gray-700 text-xs">{feature}</span>
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => handleSelectPlan(plan.name)}
                  className="block w-full py-2.5 rounded-full font-medium text-sm text-center transition-all cursor-pointer whitespace-nowrap bg-gray-100 text-gray-900 hover:bg-gray-200 hover:scale-105"
                >
                  Select This Plan
                </button>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="text-center text-gray-500 text-xs mt-8"
        >
          * Pricing varies by location and season. Contact us for details.
        </motion.p>
      </div>
    </section>
  );
}
